import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Edit, Plus, Trash2 } from "lucide-react";

import Button from "../../Components/Button";
import DropdownSelector from "../../Components/DropdownSelector";
import SearchInput from "../../Components/Search/SearchInput";
import SuccessNotification from "../../Components/SuccessNotification";
import ToggleSwitch from "../../Components/ToggleSwitch";
import DeleteModal from "../../Container/DeleteModal";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  clearDeleteAdminTipState,
  deleteAdminTip,
  fetchAdminTips,
  updateAdminTipActiveStatus,
  type AdminTip,
} from "../../Store/Reducers/AdminTipsSlice";
import { categoryLabel } from "../../Utils/sponsorTipsConstants";
import AdminTableContainer from "../AdminTableContainer";
import TipFormModal from "./TipFormModal";

const INITIAL_NOTIFICATION = {
  show: false,
  type: "success" as const,
  message: "",
  details: "",
};

const AUDIENCE_FILTER_OPTIONS = [
  { value: "all", label: "All audiences" },
  { value: "sponsor", label: "Sponsors" },
  { value: "account_team", label: "Account teams" },
  { value: "program_leader", label: "Program leaders" },
];

const TIP_TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "best_practice", label: "Best practice" },
  { value: "failure_mode", label: "Failure mode" },
];

interface AdminTipsContainerProps {
  loading?: boolean;
}

const AdminTipsContainer: React.FC<AdminTipsContainerProps> = ({
  loading: externalLoading = false,
}) => {
  const dispatch = useAppDispatch();
  const {
    tips,
    loading,
    deleteTipSuccess,
    deleteTipLoading,
    createTipSuccess,
    updateTipSuccess,
  } = useAppSelector(state => state.adminTips);

  const [searchValue, setSearchValue] = useState("");
  const [audienceFilter, setAudienceFilter] = useState("all");
  const [tipTypeFilter, setTipTypeFilter] = useState("all");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedTip, setSelectedTip] = useState<AdminTip | null>(null);
  const [notification, setNotification] = useState(INITIAL_NOTIFICATION);

  const loadTips = useCallback(() => {
    dispatch(fetchAdminTips({}) as any);
  }, [dispatch]);

  useEffect(() => {
    loadTips();
  }, [loadTips]);

  useEffect(() => {
    if (createTipSuccess || updateTipSuccess) {
      loadTips();
      setFormModalOpen(false);
      setIsEditMode(false);
      setSelectedTip(null);
    }
  }, [createTipSuccess, updateTipSuccess, loadTips]);

  useEffect(() => {
    if (deleteTipSuccess) {
      setNotification({
        show: true,
        type: "success",
        message: "Tip Deleted Successfully",
        details: "The tip has been removed from the public library.",
      });
      dispatch(clearDeleteAdminTipState());
    }
  }, [deleteTipSuccess, dispatch]);

  const handleDeleteClick = useCallback((tip: AdminTip) => {
    setSelectedTip(tip);
    setDeleteModalOpen(true);
  }, []);

  const handleEditClick = useCallback((tip: AdminTip) => {
    setSelectedTip(tip);
    setIsEditMode(true);
    setFormModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (selectedTip) {
      await dispatch(deleteAdminTip(selectedTip.id) as any);
      setDeleteModalOpen(false);
      setSelectedTip(null);
    }
  }, [dispatch, selectedTip]);

  const handleAddClick = useCallback(() => {
    setSelectedTip(null);
    setIsEditMode(false);
    setFormModalOpen(true);
  }, []);

  const handleStatusToggle = useCallback(
    async (tip: AdminTip, active: boolean) => {
      await dispatch(
        updateAdminTipActiveStatus({ tip_id: tip.id, active }) as any
      );
    },
    [dispatch]
  );

  const filteredTips = useMemo(() => {
    const searchLower = searchValue.toLowerCase();
    return tips.filter(tip => {
      const matchesSearch =
        (tip.title?.toLowerCase() || "").includes(searchLower) ||
        (tip.url_slug?.toLowerCase() || "").includes(searchLower) ||
        (tip.category?.toLowerCase() || "").includes(searchLower);
      const matchesAudience =
        audienceFilter === "all" || tip.audience === audienceFilter;
      const matchesType =
        tipTypeFilter === "all" || tip.tip_type === tipTypeFilter;
      return matchesSearch && matchesAudience && matchesType;
    });
  }, [tips, searchValue, audienceFilter, tipTypeFilter]);

  const columns = useMemo(
    () => [
      {
        key: "title",
        title: "TIP",
        width: "32%",
        render: (_: unknown, record: AdminTip) => (
          <div className="min-w-0">
            <div className="text-sm  text-gray-900 truncate">
              {record.title}
            </div>
          </div>
        ),
      },
      {
        key: "audience",
        title: "AUDIENCE",
        width: "12%",
        render: (_: unknown, record: AdminTip) => (
          <span className="text-sm text-gray-700 capitalize">
            {record.audience.replace(/_/g, " ")}
          </span>
        ),
      },
      {
        key: "category",
        title: "CATEGORY",
        width: "18%",
        render: (_: unknown, record: AdminTip) => (
          <span className="text-sm text-gray-700">
            {categoryLabel(record.category)}
          </span>
        ),
      },
      {
        key: "tip_type",
        title: "TYPE",
        width: "12%",
        render: (_: unknown, record: AdminTip) => (
          <span className="text-sm text-gray-700 capitalize">
            {record.tip_type.replace(/_/g, " ")}
          </span>
        ),
      },
      {
        key: "active",
        title: "ACTIVE",
        width: "10%",
        render: (_: unknown, record: AdminTip) => (
          <ToggleSwitch
            checked={record.active ?? true}
            onChange={checked => handleStatusToggle(record, checked)}
            id={`tip-active-${record.id}`}
          />
        ),
      },
      {
        key: "actions",
        title: "ACTIONS",
        width: "8%",
        render: (_: unknown, record: AdminTip) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleEditClick(record)}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title="Edit tip"
            >
              <Edit size={16} />
            </button>
            <button
              type="button"
              onClick={() => handleDeleteClick(record)}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Delete tip"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ),
      },
    ],
    [handleDeleteClick, handleEditClick, handleStatusToggle]
  );

  return (
    <div className="min-h-[400px]">
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 m-0">
                Guidance Library
              </h2>
              <p className="text-sm text-gray-500 m-0 mt-0.5">
                Global library content shared across all tenants
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" onClick={handleAddClick}>
              <Plus className="h-4 w-4 mr-1.5" />
              Add Tip
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] max-w-md">
            <SearchInput
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              placeholder="Search by title, slug, or category..."
            />
          </div>
          <DropdownSelector
            label="Audience"
            selected={audienceFilter}
            options={AUDIENCE_FILTER_OPTIONS}
            onSelect={setAudienceFilter}
            variant="segmented"
          />
          <DropdownSelector
            label="Type"
            selected={tipTypeFilter}
            options={TIP_TYPE_FILTER_OPTIONS}
            onSelect={setTipTypeFilter}
            variant="segmented"
          />
        </div>
      </div>

      <AdminTableContainer
        columns={columns}
        data={filteredTips}
        loading={loading || externalLoading}
        pagination={true}
      />

      <TipFormModal
        isOpen={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setIsEditMode(false);
          setSelectedTip(null);
        }}
        tip={selectedTip}
        isEditMode={isEditMode}
      />

      <DeleteModal
        isOpen={deleteModalOpen}
        toggle={() => {
          setDeleteModalOpen(false);
          setSelectedTip(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Tip"
        entityName={selectedTip?.title ? `"${selectedTip.title}"` : "this tip"}
        warningItems={[
          "This tip will be removed from the Guidance Library for all tenants.",
        ]}
        loading={deleteTipLoading}
      />

      <SuccessNotification
        isOpen={notification.show}
        type={notification.type}
        message={notification.message}
        details={notification.details}
        onClose={() => setNotification(INITIAL_NOTIFICATION)}
      />
    </div>
  );
};

export default AdminTipsContainer;
